import React, { useState, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Search, Users, Clock, LayoutDashboard, Grid, List, AlertCircle, Shield, Plus, X } from 'lucide-react';
import './dashboard.css';
import UserProfile from './UserProfile';

const PAGE_SIZE = 500;

const getStatusClass = (status) => {
  switch (status) {
    case 'Active': return 'status-badge status-active';
    case 'Inactive': return 'status-badge status-inactive';
    case 'Away': return 'status-badge status-away';
    default: return 'status-badge';
  }
};

const MemberCard = ({ member, onRemove, onRoleChange }) => {
  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState(member.role);

  const saveRole = async () => {
    if (newRole.trim() !== '' && newRole !== member.role) {
      await onRoleChange(member.id, newRole);
    }
    setEditingRole(false);
  };

  return (
    <div className="member-card">
      <div className="member-header">
        <div className="member-info">
          <div className="avatar">{member.avatar}</div>
          <div className="member-details">
            <h3>{member.name}</h3>
            <p>{member.email}</p> 
          </div>
        </div>
        <span className={getStatusClass(member.status)}>{member.status}</span>
      </div>
      <div className="member-metadata">
        <div className="metadata-row"><span className="metadata-label">Team:</span><span>{member.team}</span></div>
        <div className="metadata-row"><span className="metadata-label">Manager:</span><span>{member.manager}</span></div>
        <div className="metadata-row"><span className="metadata-label">Joined:</span><span>{member.joined}</span></div>
        <div className="metadata-row action-buttons-row">
            {editingRole ? (
                <div className="edit-role-container">
                    <input
                    className="edit-role-input"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveRole()}
                    />
                    <button className="save-button" onClick={saveRole}>
                    SAVE
                    </button>
                </div>
                ) : (
                <div className="action-buttons-row">
                    <button className="edit-button" onClick={() => setEditingRole(true)}>
                    EDIT
                    </button>
                    <button className="remove-button" onClick={() => onRemove(member.id)}>
                    REMOVE
                    </button>
                </div>
                )}
        </div>
      </div> 
    </div>
  );
};

const AddEmployeeModal = ({ isOpen, onClose, onAdd, dropdownOptions, currentManager }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    manager: currentManager || '',
    department: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        role: '',
        manager: currentManager || '',
        department: ''
      });
    }
  }, [isOpen, currentManager]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('/api/employees', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        onAdd(result.employee);
        onClose();
      } else {
        const error = await response.json();
        console.error('Failed to create employee:', error.error);
        alert('Failed to create employee: ' + error.error);
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      alert('Error creating employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add New Team Member</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter employee name"
              required
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              value={formData.role}
              onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
              placeholder="Enter role"
              required
            />
          </div>

          <div className="form-group">
            <label>Manager</label>
            <input
              type="text"
              value={formData.manager}
              onChange={e => setFormData(prev => ({ ...prev, manager: e.target.value }))}
              placeholder="Manager name"
              required
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              New employees will be assigned to you as their manager
            </small>
          </div>

          <div className="form-group">
            <label>Department</label>
            <select
              value={formData.department}
              onChange={e => setFormData(prev => ({ ...prev, department: e.target.value }))}
              required
            >
              <option value="">Select Department</option>
              {dropdownOptions.departments?.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-save">
              {loading ? 'Adding...' : 'Add Team Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DepartmentSection = ({ departmentName, data, viewMode, onRemove, onRoleChange }) => (
  <div className="department-section">
    <div className="department-header">
      <div className="department-info">
        <div className="department-icon"><div className="department-icon-inner"></div></div>
        <span className="department-title">{departmentName}</span>
      </div>
      <span className="member-count">{data.memberCount} members</span>
    </div>
    {viewMode === 'card' ? (
      <div className="member-grid">
        {data.members.map(member => (
          <MemberCard key={member.id} member={member} onRemove={onRemove} onRoleChange={onRoleChange} />
        ))}
      </div>
    ) : (
      <table className="member-table">
        <thead>
          <tr>
            <th>Name</th><th>Role</th><th>Department</th><th>Manager</th><th>Joined</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.members.map(member => (
            <tr key={member.id}>
              <td>
                <div className="list-member-info">
                  <div className="avatar-small">{member.avatar}</div>
                  <div>
                    <div className="list-member-name">{member.name}</div>
                    <div className="list-member-email">{member.email}</div>
                  </div>
                </div>
              </td>
              <td>{member.role}</td>
              <td>{member.team}</td>
              <td>{member.manager}</td>
              <td>{member.joined}</td>
              <td><span className={getStatusClass(member.status)}>{member.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function ManagerDashboard() {
  const { authState, oktaAuth } = useOktaAuth();
  const [employees, setEmployees] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All Departments');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [viewMode, setViewMode] = useState('card');
  const [error, setError] = useState(null);
  const [hasManagerAccess, setHasManagerAccess] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    managers: [],
    departments: [],
    statuses: []
  });
  const loaderRef = useRef();

  // Update dropdown options when employees change
  useEffect(() => {
    const managers = [...new Set(employees.map(emp => emp.manager))].filter(Boolean).sort();
    const departments = [...new Set(employees.map(emp => emp.department))].filter(Boolean).sort();
    const statuses = ['Active', 'Inactive', 'Away'];
    
    setDropdownOptions({
      managers,
      departments,
      statuses
    });
  }, [employees]);

  useEffect(() => {
    const initializeManagerData = async () => {
      // Check if using simple auth (demouser or manager login)
      const simpleAuth = sessionStorage.getItem('simpleAuth');
      const isAdminView = sessionStorage.getItem('isAdminView') === 'true';
      const managerInfo = sessionStorage.getItem('managerInfo');
      
      if (simpleAuth) {
        try {
          setHasManagerAccess(true);
          
          // Fetch all employees
          const response = await fetch(`http://localhost:4000/api/employees?limit=1000&offset=0`);
          if (!response.ok) {
            throw new Error('Failed to fetch employee data');
          }
          
          const allEmployees = await response.json();
          
          // If admin view (demouser), set a generic user
          if (isAdminView) {
            setCurrentUser({
              name: 'Admin User',
              employeeID: 'admin',
              title: 'Administrator'
            });
          } else if (managerInfo) {
            // Manager login - use their info
            const parsedManagerInfo = JSON.parse(managerInfo);
            setCurrentUser({
              name: parsedManagerInfo.name,
              employeeID: parsedManagerInfo.employeeId,
              title: parsedManagerInfo.title,
              department: parsedManagerInfo.department,
              email: parsedManagerInfo.email
            });
          }

          setEmployees(allEmployees);
          setInitialLoading(false);
        } catch (error) {
          console.error('Error initializing manager data:', error);
          setError('Failed to load manager data. Please try again.');
          setInitialLoading(false);
        }
      } else if (authState?.isAuthenticated) {
        try {
          // Get user info and groups from Okta
          const user = await oktaAuth.getUser();
          const groups = user.groups || [];
          setUserGroups(groups);
          
          // Check if user has any group containing 'Manager'
          const hasAccess = groups.some(group => 
            group.toLowerCase().includes('manager')
          );
          setHasManagerAccess(hasAccess);
          
          if (!hasAccess) {
            setError('Access denied. You need to be in a group containing "Manager" to access this dashboard.');
            setInitialLoading(false);
            return;
          }

          // Get user's name to identify them as a manager
          const userName = authState.idToken?.claims?.name;
          
          if (!userName) {
            setError('Unable to identify user. Please contact your administrator.');
            setInitialLoading(false);
            return;
          }

          // Find current user in employee database to get their info
          const response = await fetch(`http://localhost:4000/api/employees?limit=1000&offset=0`);
          if (!response.ok) {
            throw new Error('Failed to fetch employee data');
          }
          
          const allEmployees = await response.json();
          
          // Find current user
          const foundUser = allEmployees.find(emp => 
            emp.name.toLowerCase() === userName?.toLowerCase()
          );

          if (foundUser) {
            setCurrentUser(foundUser);
          }

          setEmployees(allEmployees);
          setInitialLoading(false);

        } catch (error) {
          console.error('Error initializing manager data:', error);
          setError('Failed to load manager data. Please try again.');
          setInitialLoading(false);
        }
      }
    };

    initializeManagerData();
  }, [authState, oktaAuth]);

  // Fetch more employees for pagination
  useEffect(() => {
    if (initialLoading || !hasManagerAccess) return;
    
    fetch(`/api/employees?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`)
      .then(res => res.json())
      .then(newRows => {
        if (page === 0) {
          setEmployees(newRows);
        } else {
          setEmployees(prev => [...prev, ...newRows]);
        }
        if (newRows.length < PAGE_SIZE) setHasMore(false);
      })
      .catch(err => {
        console.error(err);
      });
  }, [page, hasManagerAccess, initialLoading]);

  useEffect(() => {
    if (!hasMore) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setPage(p => p + 1),
      { threshold: 1.0 }
    );
    if (loaderRef.current) obs.observe(loaderRef.current);
    return () => obs.disconnect();
  }, [hasMore]);

  const handleRemove = async (memberId) => {
    try {
      const response = await fetch(`/api/employees/${memberId}`, { 
        method: 'DELETE' 
      });
      
      if (response.ok) {
        setEmployees(prev => prev.filter(emp => emp.id !== memberId));
      } else {
        console.error('Failed to remove employee');
      }
    } catch (error) {
      console.error('Error removing employee:', error);
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      const employee = employees.find(emp => emp.id === id);
      if (!employee) return;

      const response = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          role: newRole,
          manager: employee.manager,
          department: employee.department
        })
      });

      if (response.ok) {
        setEmployees(prev => prev.map(emp => 
          emp.id === id ? { ...emp, role: newRole } : emp
        ));
        console.log('Role updated successfully');
      } else {
        console.error('Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleAddEmployee = (newEmployee) => {
    // Add the new employee to the local state
    const employeeWithDefaults = {
      ...newEmployee,
      email: newEmployee.email || `${newEmployee.name.toLowerCase().replace(' ', '.')}@company.com`,
      status: 'Active',
      joined: new Date().toLocaleDateString(),
      avatar: newEmployee.name.charAt(0).toUpperCase()
    };
    
    setEmployees(prev => [...prev, employeeWithDefaults]);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  // Get the logged in manager's ID (not name!) - this is what we match against emp.manager
  const simpleAuthUser = sessionStorage.getItem('simpleAuthUser');
  const loggedInManagerId = simpleAuthUser || authState?.idToken?.claims?.preferred_username || 'Deep Adhikari';
  
  // For display purposes, get the manager's name
  const loggedInManagerName = currentUser?.name || authState?.idToken?.claims?.name || 'Manager';
  
  // Check if this is admin view (demouser)
  const isAdminView = sessionStorage.getItem('isAdminView') === 'true';

  // Debug logging
  console.log('=== MANAGER DASHBOARD DEBUG ===');
  console.log('Logged in Manager ID:', loggedInManagerId);
  console.log('Is Admin View:', isAdminView);
  console.log('Total Employees:', employees.length);
  console.log('Sample employee managers:', employees.slice(0, 5).map(e => ({ name: e.name, manager: e.manager })));

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = !searchQuery ||
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.manager.toLowerCase().includes(searchQuery.toLowerCase());

    // For admin view, don't filter by manager - show all employees
    // For manager view, filter by matching the managerid field with the logged-in manager's ID
    const matchesManager = isAdminView ? true : emp.manager === loggedInManagerId;
    const matchesDepartment = selectedDepartment === 'All Departments' || emp.department === selectedDepartment;
    const matchesStatus = selectedStatus === 'All Status' || (emp.status || 'Active') === selectedStatus;

    return matchesSearch && matchesManager && matchesDepartment && matchesStatus;
  });

  const managerData = filteredEmployees.reduce((acc, emp) => {
    const mgr = emp.manager;
    const dept = emp.department;
    if (!acc[mgr]) acc[mgr] = {};
    if (!acc[mgr][dept]) acc[mgr][dept] = { memberCount: 0, members: [] };

    acc[mgr][dept].members.push({
      id: emp.id,
      name: emp.name,
      email: emp.email || `${emp.name.toLowerCase().replace(' ', '.')}@company.com`,
      role: emp.role,
      team: dept,
      manager: emp.manager,
      joined: emp.joined || '–',
      status: emp.status || 'Active',
      avatar: emp.name.charAt(0).toUpperCase()
    });
    acc[mgr][dept].memberCount++;
    return acc;
  }, {});

  const uniqueDepartments = [...new Set(employees.map(emp => emp.department))].sort();

  if (initialLoading) return (
    <div className="dashboard-container">
      <div className="main-content">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div className="loading-spinner"></div>
          <div>Loading manager dashboard...</div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="main-content">
          <UserProfile />
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '20px',
            margin: '20px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={24} color="#d63031" />
            <div>
              <h3 style={{ margin: '0 0 8px 0', color: '#d63031' }}>Access Restricted</h3>
              <p style={{ margin: 0, color: '#666' }}>{error}</p>
              {userGroups.length > 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
                  Your current groups: {userGroups.join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="sidebar">
        <div className="logo">
          <div className="logo-icon">
            <img src="/ryder.png" alt="Ryder" className="logo-image" />
          </div>
          <span className="logo-text">Ryder</span>
        </div>
        <div>
          <div className="menu-header">MANAGER DASHBOARD</div>
          <div className="menu-item active">
            <LayoutDashboard className="menu-icon" />
            <span>Dashboard</span>
          </div>
          <div className="menu-item">
            <Users className="menu-icon" />
            <span>Participants</span>
          </div>
          <div className="menu-item">
            <Clock className="menu-icon" />
            <span>Time Tracking</span>
          </div>
        </div>
      </div>

      <div className="main-content">
        <UserProfile />
        
        <div className="header">
          <div className="header-text">
            <h1>Manage Your Team</h1>
            <p>Edit roles or remove employees by department</p>
          </div>
          <div className="header-actions">
            <button 
              className="download-btn"
              onClick={() => setIsAddModalOpen(true)}
              title="Add new team member"
            >
              <Plus className="menu-icon" />
              <span>Add Employee</span>
            </button>
            <div className="access-badge">
              <Shield size={16} />
              <span>Manager Access</span>
            </div>
          </div>
        </div>

        <div className="search-filters">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, team, etc."
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <select className="filter-select" value={selectedDepartment} onChange={e => setSelectedDepartment(e.target.value)}>
            <option>All Departments</option>
            {uniqueDepartments.map(dept => <option key={dept}>{dept}</option>)}
          </select>
          <select className="filter-select" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
            <option>All Status</option>
            <option>Active</option>
            <option>Inactive</option>
            <option>Away</option>
          </select>
          <div className="view-toggle">
            <button className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`} onClick={() => setViewMode('card')}>
              <Grid className="view-icon" /> Cards
            </button>
            <button className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
              <List className="view-icon" /> List
            </button>
          </div>
        </div>

        {Object.entries(managerData[loggedInManagerId] || {}).map(([dept, data]) => (
          <DepartmentSection
            key={dept}
            departmentName={dept}
            data={data}
            viewMode={viewMode}
            onRemove={handleRemove}
            onRoleChange={handleRoleChange}
          />
        ))}

        {Object.keys(managerData[loggedInManagerId] || {}).length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#666'
          }}>
            <Users size={64} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>No Team Members Found</h3>
            <p style={{ margin: 0 }}>
              {searchQuery ? `No employees found matching "${searchQuery}"` : 'No employees are currently assigned to you as their manager.'}
            </p>
          </div>
        )}

        {hasMore && <div ref={loaderRef} className="loader">Loading more employees...</div>}

        {/* Add Employee Modal */}
        <AddEmployeeModal
          isOpen={isAddModalOpen}
          onClose={handleCloseAddModal}
          onAdd={handleAddEmployee}
          dropdownOptions={dropdownOptions}
          currentManager={loggedInManagerId}
        />
      </div>
    </div>
  );
}
